
import { defineComponent } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getWorldmap, getSealpro } from "@/api/analytics";

interface DownlineRow {
  location: string;
  latitude: number;
  longitude: number;
  headcount: number;
  rank: string;
}

interface SummaryRow {
  key: string;
  name: string;
  count: number;
  amount: number | null;
  iconSrc: string | null;
  iconClass: string | null;
}

const COVERAGE_TYPES: Array<{
  code: string;
  label: string;
  amount: number | null;
}> = [
  { code: "BR", label: "Bronze", amount: 500000 },
  { code: "PT", label: "Platinum", amount: 1000000 },
  { code: "RP", label: "RepatCare", amount: null }
];

import DashboardTabNavigator from "@/components/dashboard/DashboardTabNavigator.vue";

export default defineComponent({
  components: {
    DashboardTabNavigator,
  },
  name: "GlobalPartnersMapCard",
  props: {
    ccode: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: true,
      summaryIconSize: 26,
      viewMode: "team" as string,
      resolvedCcode: -1 as number,
      downlines: [] as DownlineRow[],
      map: null as L.Map | null,
      markerLayer: null as L.LayerGroup | null
    };
  },
  computed: {
    showInsuranceProtection(): boolean {
      return Number(this.resolvedCcode) === 0;
    },
    totalLabel(): string {
      return this.viewMode === "insurance"
        ? "Total Protection Issued"
        : "Total Downlines";
    },
    totalDownlines(): number {
      return this.downlines.reduce((sum, row) => sum + row.headcount, 0);
    },
    protectionIssuedTotal(): number {
      return this.coverageSummary.reduce(
        (sum, row) => sum + (row.amount || 0),
        0
      );
    },
    formattedTotal(): string {
      if (this.viewMode === "insurance") {
        return this.formatAmount(this.protectionIssuedTotal);
      }
      return this.totalDownlines.toLocaleString();
    },
    summaryLabel(): string {
      return this.viewMode === "insurance" ? "By Coverage" : "By Rank";
    },
    summaryEmptyText(): string {
      return this.viewMode === "insurance"
        ? "No coverages issued yet."
        : "No downlines yet.";
    },
    rankSummary(): SummaryRow[] {
      const totals = new Map<string, number>();
      this.downlines.forEach((row) => {
        totals.set(row.rank, (totals.get(row.rank) || 0) + row.headcount);
      });

      return Array.from(totals.entries())
        .map(([rank, count]) => ({
          key: rank,
          name: rank === "DI" ? "Distributor" : rank,
          count,
          amount: null,
          iconSrc: this.isDistributorRank(rank) ? null : this.rankImageUrl(rank),
          iconClass: "seq-map-card__list-badge--plain"
        }))
        .sort((a, b) => b.count - a.count);
    },
    coverageSummary(): SummaryRow[] {
      return COVERAGE_TYPES.map((type) => {
        const count = this.headcountForRank(type.code);
        const marker = this.sealproMarkerConfig(type.code);
        return {
          key: type.code,
          name: type.label,
          count,
          amount: type.amount === null ? null : count * type.amount,
          iconSrc: marker ? marker.src : null,
          iconClass: marker ? marker.typeClass : null
        };
      });
    },
    summaryRows(): SummaryRow[] {
      return this.viewMode === "insurance"
        ? this.coverageSummary
        : this.rankSummary;
    },
    mapLocations(): DownlineRow[] {
      return this.downlines.filter(
        (row) => !(row.latitude === 0 && row.longitude === 0)
      );
    }
  },
  watch: {
    ccode: {
      immediate: true,
      handler(value: number | string | null) {
        this.resolveCcode(value);
      }
    },
    showInsuranceProtection(show: boolean) {
      if (!show && this.viewMode === "insurance") {
        this.viewMode = "team";
        this.fetchDownlines();
      }
    }
  },
  methods: {
    resolveCcode(value: number | string | null) {
      const hasProp =
        value !== null && value !== undefined && value !== "" && Number(value) !== -1;
      if (hasProp) {
        this.resolvedCcode = Number(value);
        return;
      }
      const stored = localStorage.getItem("ccode");
      this.resolvedCcode = stored !== null && stored !== "" ? Number(stored) : -1;
    },
    headcountForRank(rank: string): number {
      return this.downlines
        .filter((row) => row.rank === rank)
        .reduce((sum, row) => sum + row.headcount, 0);
    },
    formatAmount(value: number): string {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    dotRadius(headcount: number): number {
      if (headcount >= 1000) return 12;
      if (headcount >= 100) return 8;
      if (headcount >= 10) return 4;
      return 2;
    },
    locationTooltip(row: DownlineRow): string {
      if (this.viewMode === "insurance") {
        const label = row.headcount === 1 ? "coverage" : "coverages";
        const typeLabel = row.rank && row.rank !== "DI" ? ` (${row.rank})` : "";
        return `${row.location}${typeLabel} - ${row.headcount} ${label}`;
      }
      const label = row.headcount === 1 ? "downline" : "downlines";
      const rankLabel = row.rank !== "DI" ? ` (${row.rank})` : "";
      return `${row.location}${rankLabel} - ${row.headcount} ${label}`;
    },
    normalizeRank(raw: unknown): string {
      if (raw == null || raw === "") {
        return "DI";
      }
      const rank = String(raw).trim();
      return rank.toUpperCase() === "DI" ? "DI" : rank;
    },
    normalizeSealproType(raw: unknown): string {
      if (raw == null || raw === "") {
        return "";
      }
      return String(raw).trim().toUpperCase();
    },
    rankImageUrl(rank: string): string {
      return `media/rank_logos/${rank}_header.png`;
    },
    sealproMarkerConfig(
      sealproType: string
    ): { src: string; typeClass: string } | null {
      const type = this.normalizeSealproType(sealproType);
      const map: Record<string, { src: string; typeClass: string }> = {
        // Bronze/gold logo → deep navy contrast
        BR: {
          src: "media/ovi_coverages/br.webp",
          typeClass: "seq-map-card__sealpro-bg--br"
        },
        // Platinum/silver logo → near-black contrast
        PT: {
          src: "media/ovi_coverages/pt.webp",
          typeClass: "seq-map-card__sealpro-bg--pt"
        },
        // RepatCare logo → white contrast
        RP: {
          src: "media/ovi_coverages/rp.webp",
          typeClass: "seq-map-card__sealpro-bg--rp"
        }
      };
      return map[type] || null;
    },
    isDistributorRank(rank: string): boolean {
      return rank === "DI";
    },
    pickField(raw: Record<string, unknown>, keys: string[]): unknown {
      for (const key of keys) {
        if (raw[key] != null && raw[key] !== "") {
          return raw[key];
        }
      }
      const lowerMap: Record<string, unknown> = {};
      Object.keys(raw).forEach((key) => {
        lowerMap[key.toLowerCase()] = raw[key];
      });
      for (const key of keys) {
        const value = lowerMap[key.toLowerCase()];
        if (value != null && value !== "") {
          return value;
        }
      }
      return undefined;
    },
    extractRows(payload: unknown): Record<string, unknown>[] {
      if (!payload) {
        return [];
      }
      if (Array.isArray(payload)) {
        return payload as Record<string, unknown>[];
      }
      if (typeof payload !== "object") {
        return [];
      }
      const data = payload as Record<string, unknown>;
      const candidates = [
        data.downlines,
        data.locations,
        data.markers,
        data.sealpro,
        data.coverages,
        data.data,
        data.items,
        data.results
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate as Record<string, unknown>[];
        }
      }
      return [];
    },
    parseCoordinatePair(raw: Record<string, unknown>): {
      latitude: number;
      longitude: number;
    } {
      const latLng =
        this.pickField(raw, ["latLng", "latlng", "coords", "coordinates"]) ||
        null;

      if (Array.isArray(latLng) && latLng.length >= 2) {
        return {
          latitude: Number(latLng[0]) || 0,
          longitude: Number(latLng[1]) || 0
        };
      }

      if (latLng && typeof latLng === "object") {
        const pair = latLng as Record<string, unknown>;
        return {
          latitude:
            Number(
              this.pickField(pair, ["lat", "latitude", "0"]) ?? pair[0]
            ) || 0,
          longitude:
            Number(
              this.pickField(pair, ["lng", "lon", "longitude", "1"]) ?? pair[1]
            ) || 0
        };
      }

      return {
        latitude:
          Number(
            this.pickField(raw, ["latitude", "lat", "Latitude", "LATITUDE"])
          ) || 0,
        longitude:
          Number(
            this.pickField(raw, [
              "longitude",
              "lng",
              "lon",
              "Longitude",
              "LONGITUDE"
            ])
          ) || 0
      };
    },
    normalizeRow(raw: Record<string, unknown>): DownlineRow {
      const coords = this.parseCoordinatePair(raw);
      const headcountRaw = this.pickField(raw, [
        "headcount",
        "count",
        "total",
        "coverages",
        "coverage_count",
        "qty"
      ]);

      return {
        location: String(
          this.pickField(raw, [
            "location",
            "name",
            "city",
            "country",
            "area",
            "label"
          ]) || "Unknown"
        ),
        latitude: coords.latitude,
        longitude: coords.longitude,
        headcount: Number(headcountRaw) || 0,
        rank:
          this.viewMode === "insurance"
            ? this.normalizeSealproType(
                this.pickField(raw, ["sealpro_type", "r", "rank", "plan"])
              ) || "DI"
            : this.normalizeRank(
                this.pickField(raw, ["r", "rank", "plan", "sealpro_type"])
              )
      };
    },
    rankMarkerSize(headcount: number): number {
      if (headcount >= 1000) return 40;
      if (headcount >= 100) return 32;
      if (headcount >= 10) return 26;
      return 22;
    },
    areaKey(row: DownlineRow): string {
      return `${row.latitude.toFixed(4)}|${row.longitude.toFixed(4)}`;
    },
    spreadMarkerPositions(
      rows: DownlineRow[]
    ): Array<{ row: DownlineRow; latitude: number; longitude: number }> {
      const groups = new Map<string, DownlineRow[]>();

      rows.forEach((row) => {
        const key = this.areaKey(row);
        const group = groups.get(key) || [];
        group.push(row);
        groups.set(key, group);
      });

      const placed: Array<{ row: DownlineRow; latitude: number; longitude: number }> = [];

      groups.forEach((group) => {
        const uniqueRanks = new Set(group.map((row) => row.rank));
        const shouldSpread = group.length > 1 && uniqueRanks.size > 1;

        if (!shouldSpread) {
          group.forEach((row) => {
            placed.push({
              row,
              latitude: row.latitude,
              longitude: row.longitude
            });
          });
          return;
        }

        const centerLat =
          group.reduce((sum, row) => sum + row.latitude, 0) / group.length;
        const centerLng =
          group.reduce((sum, row) => sum + row.longitude, 0) / group.length;
        const radiusDeg = 0.008 + group.length * 0.003;
        const lngScale = Math.cos((centerLat * Math.PI) / 180) || 1;

        group
          .slice()
          .sort((a, b) => a.rank.localeCompare(b.rank))
          .forEach((row, index) => {
            const angle = (2 * Math.PI * index) / group.length - Math.PI / 2;
            placed.push({
              row,
              latitude: centerLat + radiusDeg * Math.sin(angle),
              longitude: centerLng + (radiusDeg * Math.cos(angle)) / lngScale
            });
          });
      });

      return placed;
    },
    createMapMarker(
      row: DownlineRow,
      latitude?: number,
      longitude?: number
    ): L.Layer {
      const latLng = L.latLng(latitude ?? row.latitude, longitude ?? row.longitude);

      if (this.viewMode === "insurance") {
        const sealpro = this.sealproMarkerConfig(row.rank);
        if (sealpro) {
          const size = this.rankMarkerSize(row.headcount);
          const icon = L.divIcon({
            className: "seq-map-card__rank-icon seq-map-card__sealpro-icon",
            html: `<span class="seq-map-card__sealpro-bg ${sealpro.typeClass}" style="width:${size}px;height:${size}px;"><img src="${sealpro.src}" alt="${row.rank}" /></span>`,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
            popupAnchor: [0, -size / 2]
          });
          const marker = L.marker(latLng, { icon });
          marker.bindPopup(this.locationTooltip(row));
          return marker;
        }
      }

      if (this.isDistributorRank(row.rank)) {
        const marker = L.circleMarker(latLng, {
          radius: this.dotRadius(row.headcount),
          color: "#032BA6",
          fillColor: "#032BA6",
          fillOpacity: 0.9,
          weight: 2
        });
        marker.bindPopup(this.locationTooltip(row));
        return marker;
      }

      const size = this.rankMarkerSize(row.headcount);
      const icon = L.divIcon({
        className: "seq-map-card__rank-icon",
        html: `<img src="${this.rankImageUrl(row.rank)}" alt="${row.rank}" width="${size}" height="${size}" />`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2]
      });
      const marker = L.marker(latLng, { icon });
      marker.bindPopup(this.locationTooltip(row));
      return marker;
    },
    clusterCenter(rows: DownlineRow[]): { latitude: number; longitude: number } {
      const total = rows.reduce((sum, row) => sum + row.headcount, 0) || 1;
      return {
        latitude:
          rows.reduce((sum, row) => sum + row.latitude * row.headcount, 0) / total,
        longitude:
          rows.reduce((sum, row) => sum + row.longitude * row.headcount, 0) / total
      };
    },
    clusterHeadcount(rows: DownlineRow[]): number {
      return rows.reduce((sum, row) => sum + row.headcount, 0);
    },
    distanceKm(
      lat1: number,
      lon1: number,
      lat2: number,
      lon2: number
    ): number {
      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const earthRadiusKm = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.pow(Math.sin(dLat / 2), 2) +
        Math.cos(toRad(lat1)) *
          Math.cos(toRad(lat2)) *
          Math.pow(Math.sin(dLon / 2), 2);
      return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },
    clusterLocations(rows: DownlineRow[], radiusKm = 800): DownlineRow[][] {
      const clusters: DownlineRow[][] = [];

      for (const row of rows) {
        let nearestCluster: DownlineRow[] | null = null;
        let nearestDistance = Infinity;

        for (const cluster of clusters) {
          const center = this.clusterCenter(cluster);
          const distance = this.distanceKm(
            row.latitude,
            row.longitude,
            center.latitude,
            center.longitude
          );
          if (distance <= radiusKm && distance < nearestDistance) {
            nearestCluster = cluster;
            nearestDistance = distance;
          }
        }

        if (nearestCluster) {
          nearestCluster.push(row);
        } else {
          clusters.push([row]);
        }
      }

      return clusters;
    },
    mostPopulatedCluster(rows: DownlineRow[]): DownlineRow[] {
      const clusters = this.clusterLocations(rows);
      return clusters.reduce((best, cluster) =>
        this.clusterHeadcount(cluster) > this.clusterHeadcount(best) ? cluster : best
      );
    },
    focusMapOnPopulatedRegion() {
      const locations = this.mapLocations;
      if (!this.map || locations.length === 0) {
        this.map?.setView([20, 0], 2);
        return;
      }

      const topCluster = this.mostPopulatedCluster(locations);

      if (topCluster.length === 1) {
        const singleZoom = this.viewMode === "insurance" ? 2 : 4;
        this.map.setView(
          [topCluster[0].latitude, topCluster[0].longitude],
          singleZoom
        );
        return;
      }

      const bounds = L.latLngBounds([]);
      topCluster.forEach((row) => {
        bounds.extend(L.latLng(row.latitude, row.longitude));
      });
      this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
      const zoomSteps = this.viewMode === "insurance" ? 2 : 1;
      const zoomedOut = Math.max(
        this.map.getMinZoom(),
        this.map.getZoom() - zoomSteps
      );
      this.map.setZoom(zoomedOut);
    },
    destroyMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
        this.markerLayer = null;
      }
    },
    initMap() {
      const container = this.$refs.mapContainer as HTMLElement | undefined;
      if (!container) {
        return;
      }

      this.destroyMap();

      this.map = L.map(container, {
        scrollWheelZoom: false,
        zoomControl: true
      });

      const cartoApiKey = (process.env.VUE_APP_CARTO_API_KEY || "").trim();
      const cartoTileUrl = cartoApiKey
        ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(
            cartoApiKey
          )}`
        : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";

      L.tileLayer(cartoTileUrl, {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 19
      }).addTo(this.map);

      this.markerLayer = L.layerGroup().addTo(this.map);

      this.spreadMarkerPositions(this.mapLocations).forEach((placed) => {
        this.createMapMarker(placed.row, placed.latitude, placed.longitude).addTo(
          this.markerLayer as L.LayerGroup
        );
      });

      if (this.mapLocations.length > 0) {
        this.focusMapOnPopulatedRegion();
      } else {
        this.map.setView([20, 0], 2);
      }

      this.$nextTick(() => {
        this.map?.invalidateSize();
      });
    },
    setViewMode(mode: string) {
      if (this.viewMode === mode) {
        return;
      }
      if (mode === "insurance" && !this.showInsuranceProtection) {
        return;
      }
      this.viewMode = mode;
      this.fetchDownlines();
    },
    async fetchDownlines() {
      this.loading = true;
      this.destroyMap();
      try {
        const response =
          this.viewMode === "insurance"
            ? await getSealpro()
            : await getWorldmap();
        const rows = this.extractRows(response.data);

        this.downlines = rows.map((row: Record<string, unknown>) =>
          this.normalizeRow(row)
        );
      } catch (error) {
        this.downlines = [];
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.initMap();
        });
      }
    }
  },
  mounted() {
    this.resolveCcode(this.ccode);
    this.fetchDownlines();
  },
  beforeUnmount() {
    this.destroyMap();
  }
});
